import React, { useEffect, useRef, useState } from 'react';
import { MessageSquarePlus, Highlighter, AlertCircle } from 'lucide-react';
import { ANNOTATION_QUOTE_EVENT } from './AnnotationsPanel';

/**
 * Floating "Annotate selection" toolbar for track-changes-style review.
 *
 * Wraps a content region (markdown preview, block editor canvas, etc.) and
 * watches for non-empty text selections. When the user selects 4+ characters,
 * a small toolbar appears anchored to the end of the selection. Clicking a
 * button dispatches a global `nn-annotation-quote` event with the highlighted
 * text, which `AnnotationsPanel` listens for and pre-fills.
 *
 * No prop-drilling required between editor surfaces and the side panel.
 */
export default function InlineSelectionToolbar({ children, className = '' }) {
  const wrapperRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [quote, setQuote] = useState('');

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return undefined;

    function onSelect() {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        setPos(null);
        return;
      }
      const range = sel.getRangeAt(0);
      if (!node.contains(range.commonAncestorContainer)) {
        setPos(null);
        return;
      }
      const text = sel.toString().trim();
      if (text.length < 4) {
        setPos(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      const wrapperRect = node.getBoundingClientRect();
      setQuote(text);
      setPos({
        top: rect.bottom - wrapperRect.top + 6,
        left: Math.min(rect.right - wrapperRect.left, wrapperRect.width - 230),
      });
    }

    node.addEventListener('mouseup', onSelect);
    node.addEventListener('keyup', onSelect);
    document.addEventListener('selectionchange', onSelect);
    return () => {
      node.removeEventListener('mouseup', onSelect);
      node.removeEventListener('keyup', onSelect);
      document.removeEventListener('selectionchange', onSelect);
    };
  }, []);

  function fire(annotation_type) {
    if (!quote) return;
    window.dispatchEvent(
      new CustomEvent(ANNOTATION_QUOTE_EVENT, {
        detail: { quote, annotation_type },
      }),
    );
    setPos(null);
    window.getSelection()?.removeAllRanges();
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid="inline-selection-host">
      {children}
      {pos && (
        <div
          role="toolbar"
          aria-label="Annotate selection"
          data-testid="inline-selection-toolbar"
          className="absolute z-30 flex gap-1 rounded-lg border border-[#E7E0D6] bg-white p-1 shadow-lg"
          style={{ top: Math.max(0, pos.top), left: Math.max(0, pos.left) }}
        >
          <button
            type="button"
            onClick={() => fire('comment')}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-[#F5F0E8]"
            title="Add a comment on this passage"
          >
            <MessageSquarePlus className="w-3.5 h-3.5 text-[#1E5A8A]" />
            Comment
          </button>
          <button
            type="button"
            onClick={() => fire('highlight')}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-[#F5F0E8]"
            title="Highlight without comment"
          >
            <Highlighter className="w-3.5 h-3.5 text-[#B89B5E]" />
            Highlight
          </button>
          <button
            type="button"
            onClick={() => fire('legal')}
            className="flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-[#F5F0E8]"
            title="Flag for legal review"
          >
            <AlertCircle className="w-3.5 h-3.5 text-[#B3261E]" />
            Legal
          </button>
        </div>
      )}
    </div>
  );
}
